import { z } from 'zod';
import { SCREEN_ORIENTATIONS, SYNC_STATES } from '../constants.js';
import { playerManifestSchema, playerSettingsSchema } from '../manifest.js';

/** Device-facing API contract. Kept separate from the dashboard contract on purpose. */

export const deviceInfoSchema = z.object({
  platform: z.string().max(60),
  osVersion: z.string().max(120).optional().nullable(),
  model: z.string().max(120).optional().nullable(),
  arch: z.string().max(40).optional().nullable(),
  playerVersion: z.string().max(40),
  /** Stable hardware-derived identifier; used to re-pair the same box gracefully. */
  hardwareId: z.string().max(120).optional().nullable(),
  width: z.number().int().positive().max(16000).optional(),
  height: z.number().int().positive().max(16000).optional(),
  orientation: z.enum(SCREEN_ORIENTATIONS).optional(),
});
export type DeviceInfo = z.infer<typeof deviceInfoSchema>;

export const pairRequestSchema = z.object({ device: deviceInfoSchema });

export const pairRequestResponseSchema = z.object({
  code: z.string(),
  pollToken: z.string(),
  expiresAt: z.string(),
  pollIntervalSec: z.number().int().positive(),
});

export const pairStatusQuerySchema = z.object({ pollToken: z.string().min(10).max(200) });

export const pairStatusResponseSchema = z.discriminatedUnion('status', [
  z.object({ status: z.literal('pending'), expiresAt: z.string() }),
  z.object({ status: z.literal('expired') }),
  z.object({
    status: z.literal('paired'),
    deviceToken: z.string(),
    screenId: z.string(),
    screenName: z.string(),
    organizationId: z.string(),
    organizationName: z.string(),
    apiBaseUrl: z.string().optional(),
  }),
]);

export const heartbeatSchema = z.object({
  status: z.enum(['playing', 'idle', 'syncing', 'error']).default('playing'),
  playerVersion: z.string().max(40),
  platform: z.string().max(60).optional(),
  osVersion: z.string().max(120).optional().nullable(),
  width: z.number().int().positive().max(16000).optional(),
  height: z.number().int().positive().max(16000).optional(),
  orientation: z.enum(SCREEN_ORIENTATIONS).optional(),
  storageTotalBytes: z.number().int().nonnegative().optional(),
  storageFreeBytes: z.number().int().nonnegative().optional(),
  cacheBytes: z.number().int().nonnegative().optional(),
  /** The content version the player currently has ACTIVE (not the one it is downloading). */
  activeContentVersion: z.string().max(80).nullable().optional(),
  syncState: z.enum(SYNC_STATES).optional(),
  currentPlaylistId: z.string().uuid().nullable().optional(),
  currentMediaId: z.string().uuid().nullable().optional(),
  currentScheduleId: z.string().uuid().nullable().optional(),
  uptimeSec: z.number().int().nonnegative().optional(),
  errorCount: z.number().int().nonnegative().optional(),
  /** Free-form health metrics (cpu, memory, temperature…). */
  health: z.record(z.string(), z.unknown()).optional(),
});
export type HeartbeatInput = z.infer<typeof heartbeatSchema>;

export const playerCommandSchema = z.object({
  id: z.string(),
  command: z.string(),
  payload: z.record(z.string(), z.unknown()).nullable().optional(),
  createdAt: z.string(),
});
export type PlayerCommand = z.infer<typeof playerCommandSchema>;

export const heartbeatResponseSchema = z.object({
  serverTime: z.string(),
  /** The version the screen SHOULD have. Differs ⇒ fetch the manifest. */
  contentVersion: z.string(),
  configVersion: z.string(),
  settings: playerSettingsSchema.optional(),
  commands: z.array(playerCommandSchema).default([]),
  appUpdate: z
    .object({
      version: z.string(),
      url: z.string(),
      mandatory: z.boolean(),
      notes: z.string().optional(),
    })
    .nullable()
    .optional(),
});
export type HeartbeatResponse = z.infer<typeof heartbeatResponseSchema>;

export const playerConfigResponseSchema = z.object({
  screenId: z.string(),
  screenName: z.string(),
  organizationId: z.string(),
  organizationName: z.string(),
  locationName: z.string().nullable(),
  timezone: z.string(),
  orientation: z.enum(SCREEN_ORIENTATIONS),
  width: z.number().int(),
  height: z.number().int(),
  settings: playerSettingsSchema,
  contentVersion: z.string(),
});

export const manifestResponseSchema = playerManifestSchema;

export const syncStatusSchema = z.object({
  state: z.enum(SYNC_STATES),
  /** The manifest version this report refers to. */
  contentVersion: z.string().max(80).nullable().optional(),
  activeContentVersion: z.string().max(80).nullable().optional(),
  filesTotal: z.number().int().nonnegative().optional(),
  filesCompleted: z.number().int().nonnegative().optional(),
  bytesTotal: z.number().int().nonnegative().optional(),
  bytesCompleted: z.number().int().nonnegative().optional(),
  message: z.string().max(1000).optional().nullable(),
  errors: z
    .array(z.object({ mediaId: z.string().optional(), message: z.string().max(500) }))
    .max(50)
    .optional(),
});

export const proofOfPlayEntrySchema = z.object({
  /** Client-generated id so retried batches are idempotent. */
  eventId: z.string().max(80),
  mediaId: z.string().uuid().nullable().optional(),
  playlistId: z.string().uuid().nullable().optional(),
  slideshowId: z.string().uuid().nullable().optional(),
  scheduleId: z.string().uuid().nullable().optional(),
  startedAt: z.string(),
  completedAt: z.string(),
  durationMs: z.number().int().nonnegative(),
  completed: z.boolean().default(true),
});
export const proofOfPlayBatchSchema = z.object({
  events: z.array(proofOfPlayEntrySchema).min(1).max(1000),
});

export const playerErrorSchema = z.object({
  events: z
    .array(
      z.object({
        eventId: z.string().max(80),
        level: z.enum(['info', 'warning', 'error', 'critical']).default('error'),
        code: z.string().max(80),
        message: z.string().max(2000),
        context: z.record(z.string(), z.unknown()).nullable().optional(),
        occurredAt: z.string(),
      }),
    )
    .min(1)
    .max(200),
});

export const commandAckSchema = z.object({
  commandId: z.string().uuid(),
  status: z.enum(['done', 'failed']),
  message: z.string().max(500).optional().nullable(),
});

export const screenshotUploadResponseSchema = z.object({
  ok: z.literal(true),
  url: z.string(),
  capturedAt: z.string(),
});
